import { useCustom } from '@refinedev/core';
import { Table, Tag, Modal, Input, Select, message } from 'antd';
import { Plus, Swords, Mail, Search, PenLine, ExternalLink } from 'lucide-react';
import { useState, type FC } from 'react';

interface Prospect {
  id: string;
  companyName: string;
  contactName: string;
  contactLinkedIn: string | null;
  sector: string;
  status: string;
  source: string;
  notes: string | null;
  createdAt: string;
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  new: { color: 'cyan', label: 'Nouveau' },
  contacted: { color: 'blue', label: 'Contacté' },
  relanced: { color: 'orange', label: 'Relancé' },
  negotiating: { color: 'purple', label: 'Négociation' },
  converted: { color: 'green', label: 'Converti' },
  lost: { color: 'red', label: 'Perdu' },
};

const SOURCE_CONFIG: Record<string, { icon: FC<{ className?: string }>; color: string; label: string }> = {
  battle_card: { icon: Swords, color: 'text-blue-500', label: 'Battle Card' },
  cold_outreach: { icon: Mail, color: 'text-green-500', label: 'Cold Outreach' },
  profiler: { icon: Search, color: 'text-violet-500', label: 'Profiler' },
  manual: { icon: PenLine, color: 'text-slate-500', label: 'Manuel' },
};

// Mock data – replaced when backend LOT 26 is ready
const MOCK_PROSPECTS: Prospect[] = [
  { id: '1', companyName: 'TechCorp SAS', contactName: 'Marie Dupont', contactLinkedIn: 'https://linkedin.com/in/marie-dupont', sector: 'SaaS', status: 'contacted', source: 'profiler', notes: null, createdAt: '2026-02-15T10:00:00Z' },
  { id: '2', companyName: 'InnovaLab', contactName: 'Jean Martin', contactLinkedIn: null, sector: 'FinTech', status: 'new', source: 'cold_outreach', notes: null, createdAt: '2026-02-18T14:30:00Z' },
  { id: '3', companyName: 'DataFlow Inc.', contactName: 'Sophie Bernard', contactLinkedIn: 'https://linkedin.com/in/sophie-b', sector: 'Analytics', status: 'negotiating', source: 'battle_card', notes: null, createdAt: '2026-02-10T09:00:00Z' },
  { id: '4', companyName: 'CloudFirst', contactName: 'Pierre Leroy', contactLinkedIn: null, sector: 'Cloud', status: 'converted', source: 'manual', notes: null, createdAt: '2026-02-01T11:00:00Z' },
  { id: '5', companyName: 'StartupXYZ', contactName: 'Emma Petit', contactLinkedIn: 'https://linkedin.com/in/emma-p', sector: 'EdTech', status: 'lost', source: 'cold_outreach', notes: null, createdAt: '2026-01-28T16:00:00Z' },
];

const ProspectPipelineTable: FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ companyName: '', contactName: '', contactLinkedIn: '', sector: '', source: 'manual', notes: '' });

  const { data, isLoading } = useCustom<{ data: Prospect[] }>({
    url: '/api/v1/admin/marketing/prospect-pipeline',
    method: 'get',
    queryOptions: { retry: false },
  });

  const rawProspects = data?.data?.data;
  const prospects = Array.isArray(rawProspects) ? rawProspects : MOCK_PROSPECTS;

  const handleAddProspect = () => {
    message.success('Prospect ajouté avec succès');
    setModalOpen(false);
    setForm({ companyName: '', contactName: '', contactLinkedIn: '', sector: '', source: 'manual', notes: '' });
  };

  const columns = [
    {
      title: 'Entreprise',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string) => (
        <span className="font-medium text-slate-900 dark:text-white">{name}</span>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contactName',
      key: 'contactName',
      render: (name: string, record: Prospect) => (
        <div className="flex items-center gap-1.5">
          <span className="text-slate-700 dark:text-slate-300">{name}</span>
          {record.contactLinkedIn && (
            <a href={record.contactLinkedIn} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Secteur',
      dataIndex: 'sector',
      key: 'sector',
      render: (sector: string) => (
        <span className="text-slate-500 dark:text-slate-400">{sector}</span>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const cfg = STATUS_CONFIG[status] ?? { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source: string) => {
        const cfg = SOURCE_CONFIG[source];
        if (!cfg) return <span className="text-slate-400">{source}</span>;
        const SrcIcon = cfg.icon;
        return (
          <div className="flex items-center gap-1.5" title={cfg.label}>
            <SrcIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
            <span className="text-xs text-slate-500 dark:text-slate-400">{cfg.label}</span>
          </div>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {new Date(date).toLocaleDateString('fr-FR')}
        </span>
      ),
    },
  ];

  return (
    <div className="fc-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Pipeline Prospects
        </h3>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Ajouter prospect
        </button>
      </div>

      <Table
        dataSource={prospects}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        className="[&_.ant-table]:!bg-transparent [&_.ant-table-thead>tr>th]:!bg-slate-50 dark:[&_.ant-table-thead>tr>th]:!bg-slate-800"
      />

      <Modal
        title="Ajouter un prospect"
        open={modalOpen}
        onOk={handleAddProspect}
        onCancel={() => setModalOpen(false)}
        okText="Ajouter"
        cancelText="Annuler"
      >
        <div className="space-y-3 py-2">
          <Input
            placeholder="Nom de l'entreprise"
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          />
          <Input
            placeholder="Nom du contact"
            value={form.contactName}
            onChange={(e) => setForm({ ...form, contactName: e.target.value })}
          />
          <Input
            placeholder="Profil LinkedIn (optionnel)"
            value={form.contactLinkedIn}
            onChange={(e) => setForm({ ...form, contactLinkedIn: e.target.value })}
          />
          <Input
            placeholder="Secteur"
            value={form.sector}
            onChange={(e) => setForm({ ...form, sector: e.target.value })}
          />
          <Select
            value={form.source}
            onChange={(value) => setForm({ ...form, source: value })}
            className="w-full"
            options={[
              { value: 'manual', label: 'Manuel' },
              { value: 'battle_card', label: 'Battle Card' },
              { value: 'cold_outreach', label: 'Cold Outreach' },
              { value: 'profiler', label: 'Profiler' },
            ]}
          />
          <Input.TextArea
            placeholder="Notes (optionnel)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ProspectPipelineTable;
